import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingTranslations } from '@/constants/meeting.translations';
import { collection, getDocs, addDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Video, Mic } from 'lucide-react';
import { toast } from 'sonner';

interface Deal {
  id: string;
  business_name: string;
}

interface CreateMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMeetingCreated: () => void;
  userId: string;
  defaultDealId?: string;
}

const CreateMeetingDialog: React.FC<CreateMeetingDialogProps> = ({
  open,
  onOpenChange,
  onMeetingCreated,
  userId,
  defaultDealId,
}) => {
  const { language } = useLanguage();
  const t = getMeetingTranslations(language);

  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    dealId: defaultDealId || '',
    scheduledAt: '',
    recordingEnabled: true,
  });

  // Load deals for selection
  useEffect(() => {
    const loadDeals = async () => {
      const snap = await getDocs(query(collection(db, 'deals'), orderBy('businessName', 'asc')));
      setDeals(snap.docs.map(d => ({
        id: d.id,
        business_name: d.data().businessName || d.data().business_name || 'Unnamed',
      })));
    };

    if (open) {
      loadDeals();
      // Set default deal if provided
      if (defaultDealId && !formData.dealId) {
        setFormData(prev => ({ ...prev, dealId: defaultDealId }));
      }
    }
  }, [open, defaultDealId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.dealId) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const roomName = `acquiz-${formData.dealId.slice(0, 8)}-${Date.now()}`;
      
      await addDoc(collection(db, 'dealMeetings'), {
   dealId: formData.dealId,
   title: formData.title,
   roomName: roomName,
   scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt) : null,
   recordingEnabled: formData.recordingEnabled,
   createdBy: userId,
   status: formData.scheduledAt ? 'scheduled' : 'active',
   createdAt: serverTimestamp(),
});

      toast.success(t.meetingCreated);
      onMeetingCreated();
      onOpenChange(false);
      setFormData({
        title: '',
        dealId: '',
        scheduledAt: '',
        recordingEnabled: true,
      });
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(t.errorCreatingMeeting);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-[#CEAB65]" />
            {t.createMeeting}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr' 
              ? 'Créez une nouvelle réunion vidéo sécurisée pour un deal.'
              : 'Create a new secure video meeting for a deal.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t.meetingTitle} *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder={language === 'fr' ? 'Ex: Négociation LOI' : 'Ex: LOI Negotiation'}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deal">{t.deal} *</Label>
            <Select
              value={formData.dealId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, dealId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={language === 'fr' ? 'Sélectionner un deal' : 'Select a deal'} />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduledAt" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t.scheduledAt}
            </Label>
            <Input
              id="scheduledAt"
              type="datetime-local"
              value={formData.scheduledAt}
              onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              {language === 'fr' 
                ? 'Laissez vide pour démarrer immédiatement'
                : 'Leave empty to start immediately'}
            </p>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{t.recordingEnabled}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Enregistrement audio pour transcription'
                    : 'Audio recording for transcription'}
                </p>
              </div>
            </div>
            <Switch
              checked={formData.recordingEnabled}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, recordingEnabled: checked }))}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-[#CEAB65] hover:bg-[#CEAB65]/90"
            >
              {isLoading 
                ? (language === 'fr' ? 'Création...' : 'Creating...') 
                : (formData.scheduledAt ? t.scheduleMeeting : t.startMeeting)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMeetingDialog;
